import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  Chip,
  IconButton,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LaunchIcon from '@mui/icons-material/Launch';
import CodeIcon from '@mui/icons-material/Code';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { profile } from '../../data/profile';
import { resumeData } from '../../data/resumeData';
import CompetencyRadar from './CompetencyRadar';
import { playClickSound, playSwitchSound } from '../../utils/soundEffects';

const RecruiterModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(profile.email).then(() => {
        setCopied(true);
        playClickSound();
        setTimeout(() => setCopied(false), 2500);
      });
    } else {
      setCopied(true);
      playClickSound();
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const flagshipSystems = resumeData.projects.slice(0, 3);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          background: isDark
            ? 'radial-gradient(ellipse at 50% -10%, rgba(229, 169, 60, 0.18) 0%, rgba(13, 17, 26, 0.98) 50%, rgba(8, 10, 15, 0.99) 100%)'
            : 'radial-gradient(ellipse at 50% -10%, rgba(184, 134, 11, 0.12) 0%, #ffffff 50%, rgba(248, 245, 238, 0.99) 100%)',
          backdropFilter: 'blur(36px) saturate(200%)',
          border: `1.5px solid ${isDark ? 'rgba(229, 169, 60, 0.45)' : 'rgba(184, 134, 11, 0.35)'}`,
          boxShadow: isDark
            ? '0 35px 100px rgba(0, 0, 0, 0.95), 0 0 60px rgba(229, 169, 60, 0.18)'
            : '0 25px 70px rgba(0, 0, 0, 0.18), 0 0 40px rgba(184, 134, 11, 0.12)',
          overflow: 'hidden',
          m: { xs: 2, sm: 'auto' },
          maxHeight: '92vh',
          position: 'relative'
        }
      }}
    >
      {/* Executive Header Banner */}
      <Box
        sx={{
          p: { xs: 2.5, sm: 3.8 },
          pb: 2.2,
          borderBottom: `1.5px solid ${isDark ? 'rgba(229, 169, 60, 0.25)' : 'rgba(184, 134, 11, 0.2)'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          background: isDark
            ? 'linear-gradient(180deg, rgba(229, 169, 60, 0.08) 0%, transparent 100%)'
            : 'linear-gradient(180deg, rgba(184, 134, 11, 0.05) 0%, transparent 100%)',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2, flexWrap: 'wrap' }}>
            <Chip
              icon={<FlashOnIcon sx={{ fontSize: 15, color: '#fbbf24 !important' }} />}
              label="RECRUITER 30-SECOND EXECUTIVE DOSSIER"
              size="small"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.68rem',
                fontWeight: 800,
                backgroundColor: isDark ? 'rgba(251, 191, 36, 0.18)' : 'rgba(217, 119, 6, 0.12)',
                color: isDark ? '#fbbf24' : '#b45309',
                borderRadius: '8px',
                border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.4)' : 'rgba(217, 119, 6, 0.3)'}`,
                boxShadow: isDark ? '0 0 12px rgba(251, 191, 36, 0.2)' : 'none',
              }}
            />
            <Chip
              label="● OPEN TO HIGH-IMPACT ROLES"
              size="small"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.66rem',
                fontWeight: 800,
                backgroundColor: isDark ? 'rgba(52, 211, 153, 0.18)' : 'rgba(16, 185, 129, 0.12)',
                color: isDark ? '#34d399' : '#059669',
                borderRadius: '8px',
                border: `1px solid ${isDark ? 'rgba(52, 211, 153, 0.4)' : 'rgba(16, 185, 129, 0.3)'}`,
                boxShadow: isDark ? '0 0 12px rgba(52, 211, 153, 0.25)' : 'none',
              }}
            />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.4rem' },
              letterSpacing: '-0.02em',
              background: isDark
                ? 'linear-gradient(135deg, #ffffff 0%, #fef3c7 40%, #e5a93c 100%)'
                : 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #b8860b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent',
            }}
          >
            {resumeData.header.name}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.92rem', mt: 0.4, fontWeight: 500 }}>
            Software Engineer <Box component="span" sx={{ color: isDark ? '#e5a93c' : '#b8860b' }}>•</Box> Systems Architecture & Low-Level Tooling <Box component="span" sx={{ color: isDark ? '#e5a93c' : '#b8860b' }}>•</Box> High-Performance Algorithms
          </Typography>
        </Box>

        <IconButton
          onClick={() => {
            playSwitchSound();
            onClose();
          }}
          size="small"
          aria-label="Close recruiter summary"
          sx={{
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '10px',
            p: 0.8,
            '&:hover': {
              color: theme.palette.text.primary,
              borderColor: theme.palette.secondary.main
            }
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Scrollable Content Body */}
      <Box sx={{ p: { xs: 2.5, sm: 3.8 }, overflowY: 'auto', maxHeight: 'calc(92vh - 170px)' }}>
        {/* Section 1: Verified Telemetry Metrics */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: 'CODEFORCES MAX RATING', val: '2013 (Candidate Master)', color: '#38bdf8', bg: 'rgba(56, 189, 248, 0.05)', border: 'rgba(56, 189, 248, 0.3)' },
            { label: 'ALGORITHMIC RIGOR', val: '1,000+ Problems Solved', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.05)', border: 'rgba(251, 191, 36, 0.3)' },
            { label: 'GLOBAL CONTEST RANK', val: 'Top 500 Global (Codeforces)', color: '#34d399', bg: 'rgba(52, 211, 153, 0.05)', border: 'rgba(52, 211, 153, 0.3)' },
            { label: 'CORE SPECIALIZATION', val: 'C/C++, POSIX & TypeScript', color: '#c084fc', bg: 'rgba(192, 132, 252, 0.05)', border: 'rgba(192, 132, 252, 0.3)' },
          ].map((stat, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  p: 2,
                  height: '100%',
                  background: isDark ? stat.bg : 'rgba(0, 0, 0, 0.02)',
                  border: `1.5px solid ${isDark ? stat.border : 'rgba(0, 0, 0, 0.12)'}`,
                  borderTop: `3.5px solid ${stat.color}`,
                  borderRadius: '14px',
                  boxShadow: isDark ? `0 6px 20px ${stat.bg}` : 'none',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${stat.border}`,
                  }
                }}
              >
                <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.64rem', color: theme.palette.text.secondary, fontWeight: 800, display: 'block', mb: 0.6, letterSpacing: '0.06em' }}>
                  {stat.label}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.96rem', color: stat.color, lineHeight: 1.3 }}>
                  {stat.val}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Section 2: Flagship Engineering Systems */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AutoAwesomeIcon sx={{ color: isDark ? '#e5a93c' : '#d97706', fontSize: 18 }} />
          <Typography variant="overline" sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 800, color: isDark ? '#e5a93c' : '#d97706', letterSpacing: '0.1em', fontSize: '0.78rem' }}>
            FLAGSHIP ENGINEERING SYSTEMS //
          </Typography>
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 4.5 }}>
          {flagshipSystems.map((proj, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  p: 2.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  background: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.9)',
                  border: `1.5px solid ${isDark ? 'rgba(229, 169, 60, 0.25)' : 'rgba(184, 134, 11, 0.2)'}`,
                  borderTop: idx === 0 ? '3.5px solid #38bdf8' : idx === 1 ? '3.5px solid #fbbf24' : '3.5px solid #c084fc',
                  borderRadius: '16px',
                  boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 6px 18px rgba(0,0,0,0.06)',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 36px rgba(229, 169, 60, 0.2)'
                  }
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '1.02rem', mb: 0.8, color: isDark ? '#f8fafc' : '#0f172a' }}>
                    {proj.title}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: isDark ? '#fbbf24' : '#b45309', display: 'block', mb: 1.4, fontWeight: 700 }}>
                    {proj.tech}
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: '0.84rem', lineHeight: 1.6, mb: 1.8 }}>
                    {proj.points[0]}
                  </Typography>
                </Box>

                <Box sx={{ pt: 1.5, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {proj.links?.github && (
                    <Button
                      href={proj.links.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={playClickSound}
                      size="small"
                      startIcon={<GitHubIcon sx={{ fontSize: 14 }} />}
                      endIcon={<LaunchIcon sx={{ fontSize: 12 }} />}
                      sx={{
                        fontSize: '0.74rem',
                        fontWeight: 800,
                        p: 0,
                        color: isDark ? '#38bdf8' : '#0284c7',
                        textTransform: 'none'
                      }}
                    >
                      View Source
                    </Button>
                  )}
                  <Button
                    component={Link}
                    to="/work"
                    onClick={() => {
                      playClickSound();
                      onClose();
                    }}
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
                    sx={{
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      p: 0,
                      color: isDark ? '#e5a93c' : '#b45309',
                      textTransform: 'none'
                    }}
                  >
                    Deep Dive
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Section 3: Interactive Competency Radar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AutoAwesomeIcon sx={{ color: isDark ? '#38bdf8' : '#0284c7', fontSize: 18 }} />
          <Typography variant="overline" sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 800, color: isDark ? '#38bdf8' : '#0284c7', letterSpacing: '0.1em', fontSize: '0.78rem' }}>
            VERIFIED COMPETENCY RADAR & TECHNICAL SCOPE //
          </Typography>
        </Box>

        <Box sx={{ mb: 4, p: { xs: 2, sm: 3 }, borderRadius: '20px', border: `1.5px solid ${isDark ? 'rgba(56, 189, 248, 0.25)' : 'rgba(2, 132, 199, 0.2)'}`, background: isDark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(248, 245, 238, 0.6)' }}>
          <CompetencyRadar />
        </Box>

        {/* Section 4: Verified External Profile Badges */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            href="https://github.com/Girish-1-Goyal"
            target="_blank"
            rel="noopener noreferrer"
            onClick={playClickSound}
            size="small"
            variant="outlined"
            startIcon={<GitHubIcon sx={{ fontSize: 16, color: isDark ? '#c084fc' : '#7c3aed' }} />}
            endIcon={<LaunchIcon sx={{ fontSize: 12 }} />}
            sx={{ borderRadius: '10px', fontSize: '0.78rem', fontWeight: 700, borderColor: theme.palette.divider, textTransform: 'none', px: 1.8, py: 0.6 }}
          >
            GitHub (Girish-1-Goyal)
          </Button>

          <Button
            href="https://codeforces.com/profile/epsilon_xd"
            target="_blank"
            rel="noopener noreferrer"
            onClick={playClickSound}
            size="small"
            variant="outlined"
            startIcon={<CodeIcon sx={{ fontSize: 16, color: isDark ? '#34d399' : '#059669' }} />}
            endIcon={<LaunchIcon sx={{ fontSize: 12 }} />}
            sx={{ borderRadius: '10px', fontSize: '0.78rem', fontWeight: 700, borderColor: theme.palette.divider, textTransform: 'none', px: 1.8, py: 0.6 }}
          >
            Codeforces (epsilon_xd)
          </Button>

          <Button
            href="https://www.linkedin.com/in/girish-kumar-goyal-189152198/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={playClickSound}
            size="small"
            variant="outlined"
            startIcon={<LinkedInIcon sx={{ fontSize: 16, color: isDark ? '#60a5fa' : '#2563eb' }} />}
            endIcon={<LaunchIcon sx={{ fontSize: 12 }} />}
            sx={{ borderRadius: '10px', fontSize: '0.78rem', fontWeight: 700, borderColor: theme.palette.divider, textTransform: 'none', px: 1.8, py: 0.6 }}
          >
            LinkedIn Profile
          </Button>
        </Box>
      </Box>

      {/* Footer Executive Actions Bar */}
      <Box
        sx={{
          p: { xs: 2.2, sm: 2.8 },
          px: { xs: 2.5, sm: 3.8 },
          borderTop: `1.5px solid ${isDark ? 'rgba(229, 169, 60, 0.25)' : 'rgba(184, 134, 11, 0.2)'}`,
          background: isDark ? 'rgba(8, 10, 15, 0.85)' : 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            href="/resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            onClick={playClickSound}
            variant="contained"
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              background: 'linear-gradient(135deg, #e5a93c 0%, #d97706 100%)',
              color: '#0a0c10',
              borderRadius: '10px',
              fontWeight: 800,
              px: 2.6,
              py: 1,
              fontSize: '0.86rem',
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(229, 169, 60, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Download Resume (PDF)
          </Button>

          <Button
            href={`mailto:${profile.email}?subject=Interview%20Invitation%20%2F%20Engineering%20Role%20Discussion`}
            onClick={playClickSound}
            variant="outlined"
            startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '10px',
              fontWeight: 700,
              px: 2.2,
              py: 0.95,
              fontSize: '0.86rem',
              textTransform: 'none',
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.secondary.main
              }
            }}
          >
            Schedule Direct Conversation
          </Button>
        </Box>

        <Button
          onClick={handleCopyEmail}
          variant="text"
          startIcon={copied ? <CheckIcon sx={{ fontSize: 18, color: '#34d399' }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.78rem',
            color: copied ? '#34d399' : theme.palette.text.secondary,
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: '8px',
            px: 1.5
          }}
        >
          {copied ? 'EMAIL COPIED TO CLIPBOARD' : profile.email}
        </Button>
      </Box>
    </Dialog>
  );
};

export default RecruiterModal;
